import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { useDebounceValue } from 'usehooks-ts';

import Debit from '@/models/Debit';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col, Cell } from '@/components/ui/DataTable/ColumnsHelpers';
import cellType from '@/components/ui/DataTable/CellTypes';
import SelectAllHeader from '@/components/ui/DataTable/SelectAllHeader';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import CustomMenu from '@/components/ui/Menu';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import useTableDensity from '@/hooks/useTableDensity';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import eyeIcon from '@/assets/icons/eye.svg';
import hourglassIcon from '@/assets/icons/hourglass.svg';
import checkCircleIcon from '@/assets/icons/check-circle.svg';
import editIcon from '@/assets/icons/edit.svg';
import deleteIcon from '@/assets/icons/delete.svg';
import AdditionalInfo from '@/pages/Donation/StandingOrders/AdditionalInfo/AdditionalInfo';
import ToggleOrderDialog from '@/pages/Donation/StandingOrders/OrderActions/ToggleOrderDialog';
import CancelOrderDialog from '@/pages/Donation/StandingOrders/OrderActions/CancelOrderDialog';
import EditBankOrderDialog from '@/pages/Donation/StandingOrders/OrderActions/EditBankOrderDialog';
import FiltersArea from '@/pages/Donation/StandingOrders/filters/FiltersArea';
import StandingOrdersSummary from '@/pages/Donation/StandingOrders/StandingOrdersSummary';
import StandingOrderCard from '@/pages/Donation/StandingOrders/StandingOrderCard';
import boardStyles from '@/pages/Donation/StandingOrders/StandingOrdersPage.module.scss';
import {
  ACTIVE_COLORS,
  LAST_CHARGE_COLORS,
  buildExportColumns,
  countAttention,
  filterBoardRows,
  makeStandingOrderSearch,
  orderColumns,
  summarizeFailures,
} from '@/pages/Donation/StandingOrders/boardColumns';
import {
  AptOrderOverride,
  BANK_STANDING_ORDER_ACTIONS_FEATURE_KEY,
  canCancelBank,
  canEditBank,
  canFreezeBank,
  canReactivateBank,
  getBankOrderState,
  lockedCollection,
  toOrderState,
} from '@/pages/Donation/StandingOrders/bankStandingOrderActions';
import {
  OrderActionNotice,
  StandingOrderActionKind,
  StandingOrderActionResult,
  actionLogUrl,
} from '@/pages/Donation/StandingOrders/standingOrderActions';
import { AttentionFilter, formatDate, getPaymentSourceLabel } from '@/pages/Donation/StandingOrders/standingOrderModel';
import { BANK_COLLECTION_LOCKS_URL, BankCollectionLockDtoIn } from '@/pages/Management/BankCollectionLocks/bankCollectionLocks';
import BulkOrderActionDialog from './BulkOrderActionDialog';
import {
  BANK_BOARD,
  BULK_ACTION_KINDS,
  BulkActionKind,
  BulkOutcome,
  DONORS_SEARCH_URL,
  DONORS_WARM_URL,
  DonorOrder,
  DonorSearchResult,
  canTakeBulk,
  countFunds,
  donorFullName,
  donorOrdersUrl,
  fundLabel,
  rowKey,
  sameOrder,
  toDonorOrders,
} from './donorStandingOrders';
import styles from './DonorStandingOrders.module.css';

const SEARCH_DEBOUNCE_MS = 350;
const MIN_QUERY_LENGTH = 2;
/** The address names the donor, so a page can be reopened or handed on: /management/donor-standing-orders?donor=1234 */
const DONOR_PARAM = 'donor';

/**
 * «הוראות קבע לפי תורם» (HAT-10024): find a donor, see every bank standing order the office keeps
 * for them in any fund, and act on one or on several at once. The office's way in when a bank
 * return covers several of a donor's collections, in several funds, which the coordinator's board
 * - one fund at a time - cannot show together.
 *
 * The board is the coordinators' bank board (Chaya, 26.10.2026: «שהטבלה תראה כמו הטבלה של
 * המשתמשים, עם הפילטרים»): its columns, filter row, summary, return banner, export and cards, from
 * boardColumns, with the fund in the place of the donor - every row is the same donor here. Not its
 * «פתיחת פנייה»: that is a coordinator's way to the office, and this is the office. Its own: the
 * fund, and several orders selected and changed together. The actions go to the board's endpoints
 * with the row's fund, so what the office does is logged under its name where the coordinator sees it.
 */
const DonorStandingOrdersPage = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const hasBankActions = useFeature(BANK_STANDING_ORDER_ACTIONS_FEATURE_KEY);
  // «מוקפאת» rather than «לא פעילה» wherever the screen itself can freeze - as on the board.
  const frozenTerm = hasBankActions;

  // ------------------------------------------------------------------ the donor
  const contactId = (() => {
    const value = Number(searchParams.get(DONOR_PARAM));
    return Number.isInteger(value) && value > 0 ? value : null;
  })();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounceValue(query.trim(), SEARCH_DEBOUNCE_MS);
  // The search result the list was opened from, for the details; null when the address named the donor.
  const [pickedDonor, setPickedDonor] = useState<DonorSearchResult | null>(null);

  /*
   * The server keeps the bank donors in memory and reads them from APT when it has no fresh copy -
   * a few seconds. Asked for as the screen opens, so it happens while the office types, not after.
   */
  const { mutate: warmSearch } = usePostAuthApi<unknown, object>(DONORS_WARM_URL);
  useEffect(() => { warmSearch({}); }, [warmSearch]);

  const canSearch = debouncedQuery.length >= MIN_QUERY_LENGTH;
  const {
    data: donors,
    isFetching: isSearching,
    isError: isSearchError,
  } = useGetAuthApi<DonorSearchResult[]>(canSearch && contactId == null ? `${DONORS_SEARCH_URL}?query=${encodeURIComponent(debouncedQuery)}` : '');

  // ------------------------------------------------------------------ the board's own state
  const [tableSearch, setTableSearch] = useState('');
  const [dateRange, setDateRange] = useState<{ from: string; to: string } | null>(null);
  const [attention, setAttention] = useState<Set<AttentionFilter>>(new Set());
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [density, setDensity] = useTableDensity('table.density.donorStandingOrders', 'normal');
  const [selectedOrder, setSelectedOrder] = useState<DonorOrder | undefined>();
  const [actionTarget, setActionTarget] = useState<{ kind: StandingOrderActionKind; order: DonorOrder } | undefined>();
  const [bulkTarget, setBulkTarget] = useState<{ kind: BulkActionKind; orders: DonorOrder[] } | undefined>();
  const [actionNotice, setActionNotice] = useState<OrderActionNotice | null>(null);

  const toggleAttention = useCallback((key: AttentionFilter) => {
    setAttention((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setDateRange(null);
    setAttention(new Set());
    setTableSearch('');
  }, []);

  const pickDonor = (donor: DonorSearchResult | null) => {
    setPickedDonor(donor);
    setSelectedOrder(undefined);
    setActionNotice(null);
    setBannerDismissed(false);
    handleClearFilters();
    const next = new URLSearchParams(searchParams);
    if (donor) next.set(DONOR_PARAM, String(donor.contactId));
    else next.delete(DONOR_PARAM);
    setSearchParams(next, { replace: true });
  };

  // ------------------------------------------------------------------ the orders
  const ordersUrl = contactId != null ? donorOrdersUrl(contactId) : '';
  const { data: debits, isLoading, isError, refetch } = useGetAuthApi<Debit[]>(ordersUrl);

  /*
   * The list comes from the Azure copy of APT, which catches up with the office's database a little
   * after a write. Until it does, a changed row shows what the server said it now reads
   * (bankStandingOrderActions.applyOverride), as on the board.
   */
  const [aptOverrides, setAptOverrides] = useState<Record<number, AptOrderOverride>>({});
  const orders = useMemo(() => toDonorOrders(debits ?? [], aptOverrides), [debits, aptOverrides]);
  const counts = useMemo(() => countFunds(orders), [orders]);

  // Opened from the address there is no search result: the name comes off the donor's orders, first name first as in the search.
  const donorName = pickedDonor
    ? donorFullName(pickedDonor)
    : (debits?.[0] ? donorFullName(debits[0]) : '') || (contactId != null ? String(contactId) : '');

  /*
   * The collection dates the office has locked before sending its file to the bank (HAT-1441): an
   * order that collects on one is not changed until the day has passed - held in its menu, its blade
   * and the bulk actions, as on the board (the server refuses it as well).
   */
  const { data: locks } = useGetAuthApi<BankCollectionLockDtoIn[]>(hasBankActions && contactId != null ? BANK_COLLECTION_LOCKS_URL : '');
  const lockDates = useMemo(() => locks?.map((lock) => lock.collectionDate), [locks]);
  const lockOf = useCallback(
    (order: DonorOrder) => (hasBankActions ? lockedCollection(order, lockDates) : null),
    [hasBankActions, lockDates],
  );

  // The blade holds the row it was opened with; when the list is read again it is handed the fresh one.
  useEffect(() => {
    if (!selectedOrder) return;
    const fresh = orders.find((order) => sameOrder(order, selectedOrder));
    if (fresh && fresh !== selectedOrder) setSelectedOrder(fresh);
  }, [orders, selectedOrder]);

  // ------------------------------------------------------------------ the filter row, as on the board
  const searching = tableSearch.trim() !== '';
  const filteredOrders = useMemo(
    () => filterBoardRows(orders, { attention, dateRange, searching }, BANK_BOARD),
    [orders, attention, dateRange, searching]);
  const attentionCounts = useMemo(() => countAttention(orders, BANK_BOARD), [orders]);
  const failureSummary = useMemo(() => summarizeFailures(orders), [orders]);
  const orderSearch = useMemo(
    () => makeStandingOrderSearch<DonorOrder>(i18n.language, t('standingOrders.autoFrozen'), (order) => [order.fundName, String(order.fundId)]),
    [i18n.language, t]);

  // ------------------------------------------------------------------ actions
  const rememberWritten = useCallback((order: DonorOrder, result: StandingOrderActionResult) => {
    const written = result.order;
    if (written) {
      setAptOverrides((current) => ({ ...current, [order.standingOrderId]: { state: written, at: Date.now() } }));
    }
    queryClient.invalidateQueries({ queryKey: [actionLogUrl(order.fundId, order)] });
  }, [queryClient]);

  /** After one order was changed from its menu or its blade: show it as written, then read the list again. */
  const handleActionDone = useCallback((order: DonorOrder, kind: StandingOrderActionKind, result: StandingOrderActionResult) => {
    rememberWritten(order, result);
    setActionNotice(kind === 'cancel' ? null : { orderId: order.standingOrderId, kind, nextDate: result.nextDate });
    queryClient.invalidateQueries({ queryKey: [ordersUrl] });
  }, [rememberWritten, queryClient, ordersUrl]);

  /** After a run over several orders: each one that went through shows as written. */
  const handleBulkDone = useCallback((_kind: BulkActionKind, outcomes: BulkOutcome[]) => {
    for (const outcome of outcomes) {
      if (outcome.status === 'done' && outcome.result) rememberWritten(outcome.order, outcome.result);
    }
    setActionNotice(null);
    queryClient.invalidateQueries({ queryKey: [ordersUrl] });
  }, [rememberWritten, queryClient, ordersUrl]);

  /** The row menu: the board's, for a bank order in APT - held, with why, while its collection is locked. */
  const rowMenuItems = (order: DonorOrder) => {
    const items: { icon?: string; text: string; onClick: () => void; danger?: boolean; disabled?: boolean; hint?: string }[] = [
      { icon: eyeIcon, text: t('standingOrders.orderActions.details'), onClick: () => setSelectedOrder(order) },
    ];
    if (!hasBankActions) return items;

    const state = getBankOrderState(order);
    const locked = lockOf(order);
    const held = locked
      ? { disabled: true, hint: t('standingOrders.orderActions.bank.locked.hint', { date: formatDate(locked, i18n.language) }) }
      : {};
    if (canFreezeBank(state) || canReactivateBank(state, order)) {
      const kind = canReactivateBank(state, order) ? 'reactivate' : 'freeze';
      items.push({
        icon: kind === 'reactivate' ? checkCircleIcon : hourglassIcon,
        text: t(`standingOrders.orderActions.${kind}`),
        onClick: () => setActionTarget({ kind, order }),
        ...held,
      });
    }
    if (canEditBank(state)) {
      items.push({ icon: editIcon, text: t('standingOrders.orderActions.edit'), onClick: () => setActionTarget({ kind: 'edit', order }), ...held });
    }
    if (canCancelBank(state)) {
      items.push({
        icon: deleteIcon,
        text: t('standingOrders.orderActions.cancel'),
        onClick: () => setActionTarget({ kind: 'cancel', order }),
        danger: !locked,
        ...held,
      });
    }
    return items;
  };

  // ------------------------------------------------------------------ the columns
  /*
   * Built on every render, not memoised: Col() and Cell() call hooks inside, so they run in the same
   * order each time - and before any early return, for the same reason.
   */
  const columnContext = { t, locale: i18n.language, frozenTerm };
  const columns: ColumnDef<DonorOrder>[] = [
    ...(hasBankActions ? [{
      id: 'select',
      header: ({ table }) => <SelectAllHeader table={table} />,
      cell: Cell(cellType.Selection),
      meta: { className: 'max-w-[48px] min-w-[48px] w-[48px]', lockLayout: true },
    } satisfies ColumnDef<DonorOrder>] : []),
    {
      // The board's first column says whose order it is; here they are all the donor's, so it says which fund.
      id: 'fund',
      header: Col(t('donorStandingOrders.columns.fund')),
      accessorFn: (order) => fundLabel(order, t),
      cell: ({ row }) => {
        const order = row.original;
        const account = getPaymentSourceLabel(order);
        const line = [
          t('donorStandingOrders.columns.fundNumber', { id: order.fundId }),
          account ? t('standingOrders.sourceAccount', { account }) : null,
        ].filter(Boolean).join(' · ');
        return (
          <span className={boardStyles.cellStack}>
            <span className={boardStyles.cellMain}>{fundLabel(order, t)}</span>
            <span className={boardStyles.subLine}>
              <span className={boardStyles.subLineText}>{line}</span>
            </span>
          </span>
        );
      },
      meta: { className: 'min-w-[200px] w-[19.4%]', mobileClassName: 'max-w-[38%] min-w-[38%] w-[38%]' },
    },
    ...orderColumns<DonorOrder>(columnContext),
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('standingOrders.actions')}</span>,
      cell: ({ row }) => (
        <span className={boardStyles.rowActions}>
          <span className={boardStyles.menuSlot} aria-label={t('standingOrders.orderActions.menuLabel')}>
            <CustomMenu items={rowMenuItems(row.original)} />
          </span>
        </span>
      ),
      meta: { className: 'min-w-[64px] w-[64px]', lockLayout: true },
    },
  ];

  // The board's sheet, with the fund first: this one spans funds.
  const exportColumns = useMemo(
    () => buildExportColumns<DonorOrder>({ t, locale: i18n.language, frozenTerm }, true, [
      { id: 'fundName', header: 'donorStandingOrders.columns.fund', accessorFn: (order) => fundLabel(order, t) },
      { id: 'fundNumber', header: 'donorStandingOrders.columns.fundId', accessorFn: (order) => String(order.fundId) },
    ]),
    [t, i18n.language, frozenTerm]);

  const selectionActions = hasBankActions
    ? BULK_ACTION_KINDS.map((kind) => ({
      label: t(`donorStandingOrders.bulk.${kind}.action`),
      onClick: (selected: DonorOrder[]) => setBulkTarget({ kind, orders: selected }),
      // Offered only when at least one selected order can take it; the dialog says which cannot.
      isVisible: (selected: DonorOrder[]) => selected.some((order) => canTakeBulk(order, kind)),
      ...(kind === 'cancel' ? { className: 'text-(--font-primary)' } : {}),
    }))
    : undefined;

  const hasFilters = Boolean(dateRange) || attention.size > 0 || searching;
  const emptyState = isError
    ? {
      title: t('donorStandingOrders.loadFailed'),
      description: t('donorStandingOrders.loadFailedDescription'),
      action: <Button onClick={() => refetch()}>{t('retry')}</Button>,
    }
    : hasFilters && orders.length > 0
      ? {
        title: t('standingOrders.noResults'),
        description: t('donorStandingOrders.filteredOut', { total: orders.length }),
        action: <Button variant="outline" onClick={handleClearFilters}>{t('clearFilters')}</Button>,
      }
      : { title: t('donorStandingOrders.noOrders'), description: t('donorStandingOrders.noOrdersDescription') };

  // ------------------------------------------------------------------ the page
  return (
    <div className="w-full flex flex-col gap-4">
      <div>
        <h1 className="heading-h3-semibold text-(--font-heading)">{t('donorStandingOrders.pageTitle')}</h1>
        <p className="body-md-regular text-(--font-body-1)">{t('donorStandingOrders.pageDescription')}</p>
      </div>

      {contactId == null ? (
        <section className="flex flex-col gap-2 max-w-[720px]">
          <SearchInput value={query} onChange={setQuery} placeholder={t('donorStandingOrders.searchPlaceholder')} />
          {!canSearch && <p className="body-sm-regular text-(--font-body-2)">{t('donorStandingOrders.searchHint')}</p>}
          {canSearch && isSearching && <p className="body-sm-regular text-(--font-body-2)">{t('donorStandingOrders.searching')}</p>}
          {canSearch && isSearchError && (
            <p role="alert" className="body-sm-regular text-(--font-primary)">{t('donorStandingOrders.searchError')}</p>
          )}
          {canSearch && !isSearching && !isSearchError && donors?.length === 0 && (
            <p className="body-sm-regular text-(--font-body-2)">{t('donorStandingOrders.noResults')}</p>
          )}
          {canSearch && !isSearching && donors && donors.length > 0 && (
            <ul className={styles.results}>
              {donors.map((donor) => (
                <li key={donor.contactId}>
                  <button type="button" className={styles.resultButton} onClick={() => pickDonor(donor)}>
                    <span className="body-md-semibold text-(--font-heading)">{donorFullName(donor) || donor.contactId}</span>
                    <span className="body-sm-regular text-(--font-body-2)">
                      {[donor.israeliId, donor.phone, donor.city].filter(Boolean).join(' · ')}
                    </span>
                    <span className={`body-sm-regular ${styles.resultCounts}`}>
                      {t('donorStandingOrders.summary.orders', { count: donor.ordersCount })}
                      {' · '}
                      {t('donorStandingOrders.summary.funds', { count: donor.fundsCount })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <>
          <section className={styles.donorCard}>
            <div className="body-md-regular text-(--font-body-1)">
              <p className="body-md-semibold text-(--font-heading)">{donorName}</p>
              <p className={styles.donorFacts}>
                {pickedDonor?.israeliId && <span>{pickedDonor.israeliId}</span>}
                {pickedDonor?.phone && <span>{pickedDonor.phone}</span>}
                {pickedDonor?.city && <span>{pickedDonor.city}</span>}
                <span>{t('donorStandingOrders.contactId')}: {contactId}</span>
                {counts.orders > 0 && (
                  <span>
                    {t('donorStandingOrders.summary.orders', { count: counts.orders })}{' '}
                    {t('donorStandingOrders.summary.funds', { count: counts.funds })}
                  </span>
                )}
              </p>
            </div>
            <Button variant="outline" onClick={() => pickDonor(null)}>{t('donorStandingOrders.changeDonor')}</Button>
          </section>

          <DataTable<DonorOrder>
            // The page scrolls, not a box inside it - as on the board.
            scrollMode="document"
            cardLayout="flat"
            renderCard={(row) => (
              <StandingOrderCard<DonorOrder>
                row={row}
                isActive={row.original === selectedOrder}
                onOpen={setSelectedOrder}
                hasInquiries={false}
                lastChargeColors={LAST_CHARGE_COLORS}
                activeColors={ACTIVE_COLORS}
                frozenTerm={frozenTerm}
                title={fundLabel(row.original, t)}
              />
            )}
            // The board's opening order: the last collection, newest first.
            sortBy={{ id: 'lastCharge', desc: true }}
            title={t('donorStandingOrders.boardTitle')}
            titleCaption={<StandingOrdersSummary data={filteredOrders} totalCount={orders.length} isLoading={isLoading} />}
            notice={
              failureSummary.failed > 0 && !isError && !bannerDismissed ? (
                <div className="min-w-0 w-full max-w-full">
                  <Alert
                    type="error"
                    inline
                    title={t('standingOrders.returnBanner', { count: failureSummary.failed })}
                    action={{
                      label: t('standingOrders.showReturned'),
                      onClick: () => {
                        setAttention(new Set(['failed']));
                        setBannerDismissed(true);
                      },
                    }}
                    onClose={() => setBannerDismissed(true)}
                    className={boardStyles.failureBannerSpacing}
                  />
                </div>
              ) : undefined
            }
            data={filteredOrders}
            getRowId={rowKey}
            isLoading={isLoading}
            columns={columns}
            exportColumns={exportColumns}
            exportFilename={`${t('donorStandingOrders.boardTitle')} - ${donorName}`}
            customGlobalSearch={orderSearch}
            searchQuery={tableSearch}
            setSearchQuery={setTableSearch}
            searchPlaceholder="standingOrders.searchStandingOrder"
            searchTooltip="donorStandingOrders.searchOrdersHint"
            density={density}
            onDensityChange={setDensity}
            twoLineRows
            columnLayoutKey="donorStandingOrders"
            handleRowClick={setSelectedOrder}
            activeRecord={selectedOrder}
            selectionActions={selectionActions}
            emptyStateContent={emptyState}
            filters={
              <div className="flex flex-col gap-5">
                <FiltersArea
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  attention={attention}
                  onToggleAttention={toggleAttention}
                  attentionCounts={attentionCounts}
                  attentionLabelKeys={{
                    failed: 'standingOrders.attention_returned',
                    expiring: 'standingOrders.attention_endingSoon',
                    frozen: frozenTerm ? 'standingOrders.attention_frozen' : 'standingOrders.attention_disabled',
                  }}
                  isLoading={isLoading}
                  onClearFilters={handleClearFilters}
                />
              </div>
            }
          />

          {selectedOrder && (
            <AdditionalInfo
              key={rowKey(selectedOrder)}
              data={selectedOrder}
              fundId={selectedOrder.fundId}
              handleXclick={() => setSelectedOrder(undefined)}
              // Nothing the site can charge behind an accounting-system order; the office does that.
              onCharge={() => undefined}
              hasBankActions={hasBankActions}
              collectionLock={lockOf(selectedOrder)}
              onAction={(kind) => setActionTarget({ kind, order: selectedOrder })}
              notice={actionNotice?.orderId === selectedOrder.standingOrderId ? actionNotice : null}
              onDismissNotice={() => setActionNotice(null)}
            />
          )}

          {actionTarget && (actionTarget.kind === 'freeze' || actionTarget.kind === 'reactivate') && (
            <ToggleOrderDialog
              key={`${actionTarget.kind}-${rowKey(actionTarget.order)}`}
              kind={actionTarget.kind}
              order={actionTarget.order}
              fundId={actionTarget.order.fundId}
              onClose={() => setActionTarget(undefined)}
              onDone={(kind, result) => {
                handleActionDone(actionTarget.order, kind, result);
                setActionTarget(undefined);
              }}
            />
          )}
          {actionTarget?.kind === 'cancel' && (
            <CancelOrderDialog
              key={`cancel-${rowKey(actionTarget.order)}`}
              order={actionTarget.order}
              state={toOrderState(getBankOrderState(actionTarget.order)) ?? 'ended'}
              fundId={actionTarget.order.fundId}
              onClose={() => setActionTarget(undefined)}
              onDone={(kind, result) => handleActionDone(actionTarget.order, kind, result)}
            />
          )}
          {actionTarget?.kind === 'edit' && (
            <EditBankOrderDialog
              key={`edit-${rowKey(actionTarget.order)}`}
              order={actionTarget.order}
              fundId={actionTarget.order.fundId}
              onClose={() => setActionTarget(undefined)}
              onDone={(kind, result) => handleActionDone(actionTarget.order, kind, result)}
            />
          )}
          {bulkTarget && (
            <BulkOrderActionDialog
              key={`bulk-${bulkTarget.kind}`}
              kind={bulkTarget.kind}
              orders={bulkTarget.orders}
              donorName={donorName}
              lockOf={lockOf}
              onClose={() => setBulkTarget(undefined)}
              onDone={handleBulkDone}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DonorStandingOrdersPage;
